// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { uploadProfileImage } from "@/lib/storage-utils";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Template, DataField, RosterField, TeamsSummary } from "@/types/packet-management";
import { TemplateLibraryTab } from "@/components/admin-dashboard/TemplateLibraryTab";
import { TemplateBuilderTab } from "@/components/admin-dashboard/TemplateBuilderTab";
import { PacketGeneratorTab } from "@/components/admin-dashboard/PacketGeneratorTab";
import Papa from 'papaparse';
import { generateTeamQRCode } from '@/utils/qrGenerator';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PacketManagementPage: React.FC = () => {
  // Event and template state
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [events, setEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [dataFields, setDataFields] = useState<DataField[]>([]);
  const [rosterFields, setRosterFields] = useState<RosterField[]>([]);
  const [sports, setSports] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Generate Packets state
  const [teams, setTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPackets, setGeneratedPackets] = useState<{[key: string]: string}>({});
  const [selectedPacketTemplate, setSelectedPacketTemplate] = useState<string>("");
  const [eventAgeGroups, setEventAgeGroups] = useState<string[]>([]);
  const [teamsSummary, setTeamsSummary] = useState<TeamsSummary>({ totalTeams: 0, ageGroupCount: 0, totalPlayers: 0 });
  const [generatedPdfBlob, setGeneratedPdfBlob] = useState<Blob | null>(null);
  const [generatedFilename, setGeneratedFilename] = useState<string>('');

  const setPurchasablePacket = async (eventId: string, pdfBlob: Blob, filename: string) => {
    console.log('🔄 setPurchasablePacket called with:', { eventId, filename, blobSize: pdfBlob.size });
    
    try {
      // Upload PDF to Supabase storage
      console.log('📤 Uploading to storage...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('purchasable-packets')
        .upload(`events/${eventId}/${filename}`, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        throw uploadError;
      }

      console.log('✅ Upload successful:', uploadData);

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('purchasable-packets')
        .getPublicUrl(`events/${eventId}/${filename}`);

      console.log('🔗 Public URL:', publicUrl);

      // Update event with purchasable packet URL
      console.log('💾 Updating event record...');
      const { error: updateError } = await supabase
        .from('events')
        .update({ purchasable_packet_url: publicUrl })
        .eq('id', eventId);

      if (updateError) {
        console.error('❌ Update error:', updateError);
        throw updateError;
      }

      console.log('✅ Event updated successfully');
      toast.success('PDF set as purchasable packet for this event');
    } catch (error) {
      console.error('❌ Error setting purchasable packet:', error);
      toast.error('Failed to set purchasable packet');
    }
  };

  // Template management state
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [newTemplate, setNewTemplate] = useState<Partial<Template>>({
    name: "",
    description: "",
    category: "Basic",
    isPremium: false,
    sports: [],
    isActive: true,
    type: "roster",
    layout: "one-page-per-team",
    selectedFields: [],
    headerText: "",
    footerText: "",
    price: 0
  });

  // Template builder state
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [templateLayout, setTemplateLayout] = useState<'one-page-per-team' | 'condensed' | 'detailed'>('one-page-per-team');
  const [headerText, setHeaderText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [includePageNumbers, setIncludePageNumbers] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templatePrice, setTemplatePrice] = useState(0);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  // Constants
  const categories = ["all", "Basic", "Premium", "Sport-Specific"];
  const fieldCategories = ["Player Info", "Team Info", "Statistics", "Event Details", "Contact Info"];

  // Computed values
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch all data on component mount
  useEffect(() => {
    fetchEvents();
    fetchTemplates();
    fetchSports();
    fetchRosterFields();
  }, []);

  // Update data fields when roster fields change
  useEffect(() => {
    if (rosterFields.length > 0) {
      fetchDataFields();
    }
  }, [rosterFields]);

  // Fetch teams when event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchEventTeams();
      fetchEventAgeGroups().then(ageGroups => {
        if (ageGroups.length > 0) {
          setEventAgeGroups(ageGroups);
        }
      });
      // Set default template to "Hoops" when event is selected
      if (!selectedPacketTemplate && templates.length > 0) {
        const hoopsTemplate = templates.find(t => t.name.toLowerCase().includes('hoops')) || templates[0];
        setSelectedPacketTemplate(hoopsTemplate.id);
      }
    } else {
      setTeams([]);
      setEventAgeGroups([]);
      setTeamsSummary({ totalTeams: 0, ageGroupCount: 0, totalPlayers: 0 });
    }
  }, [selectedEvent, templates]);

  // Update teams summary when teams or selected age group changes
  useEffect(() => {
    if (teams.length > 0) {
      const filteredTeams = getFilteredTeams();
      const uniqueAgeGroups = new Set(filteredTeams.map(team => team.age_group || 'Mixed Age Group'));
      const totalPlayers = filteredTeams.reduce((sum, team) => sum + (team.roster_count || 0), 0);
      
      setTeamsSummary({
        totalTeams: filteredTeams.length,
        ageGroupCount: uniqueAgeGroups.size,
        totalPlayers: totalPlayers
      });
    } else {
      setTeamsSummary({ totalTeams: 0, ageGroupCount: 0, totalPlayers: 0 });
    }
  }, [teams, selectedAgeGroup]);

  // API Functions
  const fetchEvents = async () => {
    try {
      setLoadingEvents(true);
      const { data, error } = await supabase
        .from('events')
        .select('id, name, start_date, end_date, status')
        .eq('status', 'published')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data: dbTemplates, error } = await supabase
        .from('packet_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const convertedTemplates: Template[] = (dbTemplates || []).map((template) => {
        let lastModified: string;
        try {
          if (template.updated_at) {
            const updatedDate = new Date(template.updated_at);
            lastModified = !isNaN(updatedDate.getTime()) 
              ? updatedDate.toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0];
          } else {
            lastModified = new Date().toISOString().split('T')[0];
          }
        } catch (error) {
          lastModified = new Date().toISOString().split('T')[0];
        }
        
        return {
          id: template.id,
          name: template.name,
          description: template.description || "No description",
          category: template.is_default ? "Basic" : "Custom",
          thumbnail: "",
          isPremium: (template.price || 0) > 0,
          sports: template.sports || ["All Sports"],
          isActive: template.is_active !== false,
          lastModified,
          type: "roster" as const,
          layout: (template.layout as 'one-page-per-team' | 'condensed' | 'detailed') || 'one-page-per-team',
          selectedFields: template.columns || [],
          headerText: template.header_text || "",
          footerText: template.footer_text || "",
          includePageNumbers: template.include_page_numbers || false,
          logoUrl: template.logo_url || "",
          price: template.price || 0
        };
      });

      setTemplates(convertedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const { data, error } = await supabase
        .from('sports')
        .select('name')
        .order('name');

      if (error) throw error;
      setSports(data?.map(sport => sport.name) || []);
    } catch (error) {
      console.error('Error fetching sports:', error);
    }
  };

const fetchRosterFields = async () => {
  const defaultFields: RosterField[] = [
    { name: "head_coach_name", label: "Head Coach Name", type: "text", category: "Team Info" },
    { name: "head_coach_email", label: "Head Coach Email", type: "email", category: "Contact Info" },
    { name: "head_coach_cell", label: "Head Coach Cell", type: "tel", category: "Contact Info" },
    { name: "name", label: "Team Name", type: "text", category: "Team Info" },
    { name: "age_group", label: "Age Group", type: "text", category: "Team Info" },
    { name: "first_name", label: "First Name", type: "text", category: "Player Info" },
    { name: "last_name", label: "Last Name", type: "text", category: "Player Info" },
    { name: "position", label: "Position", type: "text", category: "Player Info" },
    { name: "jersey_number", label: "Jersey Number", type: "number", category: "Player Info" },
    { name: "grad_year", label: "Graduation Year", type: "number", category: "Player Info" },
    { name: "email", label: "Player Email", type: "email", category: "Contact Info" },
    { name: "cell_phone", label: "Player Cell", type: "tel", category: "Contact Info" }
  ];
  setRosterFields(defaultFields);
};

  const fetchDataFields = async () => {
    const mappedFields: DataField[] = rosterFields.map((field, index) => ({
      id: `field_${index}`,
      name: field.name,
      label: field.label,
      type: field.type,
      isRequired: field.name === "first_name" || field.name === "last_name",
      isCustom: false,
      category: field.category,
      sortOrder: index
    }));
    setDataFields(mappedFields);
  };

  const fetchEventTeams = async () => {
    try {
      setLoadingTeams(true);
      const allTeams: any[] = [];
      
      console.log('fetchEventTeams called with selectedEvent:', selectedEvent);
      
      // Source 1: Query club_team_event_registrations (old system)
      const { data: oldSystemTeams, error: oldError } = await supabase
        .from('club_team_event_registrations')
        .select(`
          id,
          event_id,
          team_id,
          status,
          coach_teams!inner (
            id,
            name,
            head_coach_name,
            head_coach_email,
            age_group,
            sport_id
          )
        `)
        .eq('event_id', selectedEvent);

      console.log('Old system teams:', oldSystemTeams, 'Error:', oldError);

      if (!oldError && oldSystemTeams) {
        for (const registration of oldSystemTeams) {
          const team = (registration as any).coach_teams;
          const { count: rosterCount } = await supabase
            .from('coach_team_rosters')
            .select('id', { count: 'exact', head: true })
            .eq('team_id', team.id);

          allTeams.push({
            id: registration.id,
            name: team.name || 'Unnamed Team',
            coach_name: team.head_coach_name || 'Unknown Coach',
            coach_email: team.head_coach_email || '', // Include coach_email
            sport_name: 'Event Registration',
            roster_count: rosterCount || 0,
            age_group: team.age_group || 'Mixed',
            team_id: team.id
          });
        }
      }
      
      // Source 2: Query registrations table (new system) for team registrations
      const { data: newSystemRegs, error: newError } = await supabase
        .from('registrations')
        .select('*')
        .eq('event_id', selectedEvent)
        .eq('registration_type', 'team')
        .eq('status', 'confirmed');

      console.log('New system registrations:', newSystemRegs, 'Error:', newError);

      if (!newError && newSystemRegs) {
        for (const reg of newSystemRegs) {
          const registrantData = reg.registrant_data as any;
          const teamName = registrantData?.team_name || registrantData?.team?.name || 'Unnamed Team';
          const coachName = registrantData?.coach_name || registrantData?.coach?.name || 'Unknown Coach';
          const coachEmail = registrantData?.coach_email || ''; // Extract coach_email from top level
          const ageGroup = registrantData?.age_group || registrantData?.division || 'Mixed';
          const teamId = registrantData?.team_id; // team_id is stored directly in registrant_data
          
          let rosterCount = 0;
          if (teamId) {
            const { count } = await supabase
              .from('coach_team_rosters')
              .select('id', { count: 'exact', head: true })
              .eq('team_id', teamId);
            rosterCount = count || 0;
          }

          allTeams.push({
            id: reg.id,
            name: teamName,
            coach_name: coachName,
            coach_email: coachEmail, // Include coach_email in teams data
            sport_name: 'Event Registration',
            roster_count: rosterCount,
            age_group: ageGroup,
            team_id: teamId || null
          });
        }
      }

      console.log('Total teams found:', allTeams.length, allTeams);
      setTeams(allTeams);
    } catch (error) {
      console.error('Error fetching event teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchEventAgeGroups = async (): Promise<string[]> => {
    try {
      const { data: registrations, error } = await supabase
        .from('club_team_event_registrations')
        .select(`
          coach_teams!inner (age_group)
        `)
        .eq('event_id', selectedEvent)
        .eq('status', 'confirmed');

      if (error) throw error;

      const ageGroups = [...new Set(
        (registrations || [])
          .map((reg: any) => reg.coach_teams?.age_group)
          .filter(Boolean)
      )];

      return ageGroups;
    } catch (error) {
      console.error('Error fetching age groups:', error);
      return [];
    }
  };

  // Helper functions
  const getFilteredTeams = () => {
    if (selectedAgeGroup === "all") {
      return teams;
    }
    return teams.filter(team => team.age_group === selectedAgeGroup);
  };

  // Template management functions
  const editTemplate = (template: Template) => {
  // Load template data
  setEditingTemplate(template);
  setSelectedFields(template.selectedFields || []);
  setTemplateName(template.name);
  setTemplatePrice(template.price || 0);
  setTemplateLayout(template.layout);
  setHeaderText(template.headerText || "");
  setFooterText(template.footerText || "");
  setIncludePageNumbers(template.includePageNumbers || false);
  setLogoUrl(template.logoUrl || "");
  setEditingTemplateId(template.id);
  
  // Auto-switch to Builder tab
  const builderTab = document.querySelector('[data-value="builder"]') as HTMLElement;
  if (builderTab) {
    builderTab.click();
  }
  
  toast.success(`Template "${template.name}" loaded for editing`);
};
  

  const duplicateTemplate = async (template: Template) => {
    const duplicatedTemplate = {
      ...template,
      id: undefined,
      name: `${template.name} (Copy)`,
      lastModified: new Date().toISOString().split('T')[0]
    };
    
    try {
      const { data, error } = await supabase
        .from('packet_templates')
        .insert({
          name: duplicatedTemplate.name,
          description: duplicatedTemplate.description,
          is_default: false,
          price: duplicatedTemplate.price,
          sports: duplicatedTemplate.sports,
          is_active: duplicatedTemplate.isActive,
          layout: duplicatedTemplate.layout,
          columns: duplicatedTemplate.selectedFields,
          header_text: duplicatedTemplate.headerText,
          footer_text: duplicatedTemplate.footerText,
          include_page_numbers: duplicatedTemplate.includePageNumbers,
          logo_url: duplicatedTemplate.logoUrl
        })
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const { error } = await supabase
        .from('packet_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const updateTemplate = async () => {
    if (!editingTemplate) return;

    try {
      const { error } = await supabase
        .from('packet_templates')
        .update({
          name: editingTemplate.name,
          description: editingTemplate.description,
          price: editingTemplate.price,
          sports: editingTemplate.sports,
          is_active: editingTemplate.isActive,
          layout: editingTemplate.layout,
          columns: editingTemplate.selectedFields,
          header_text: editingTemplate.headerText,
          footer_text: editingTemplate.footerText,
          include_page_numbers: editingTemplate.includePageNumbers,
          logo_url: editingTemplate.logoUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingTemplate.id);

      if (error) throw error;
      
      toast.success('Template updated successfully');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const createTemplate = async () => {
    try {
      const { data, error } = await supabase
        .from('packet_templates')
        .insert({
          name: newTemplate.name,
          description: newTemplate.description,
          is_default: false,
          price: newTemplate.price,
          sports: newTemplate.sports,
          is_active: newTemplate.isActive,
          layout: newTemplate.layout,
          columns: newTemplate.selectedFields,
          header_text: newTemplate.headerText,
          footer_text: newTemplate.footerText,
          include_page_numbers: newTemplate.includePageNumbers,
          logo_url: newTemplate.logoUrl
        })
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Template created successfully');
      setNewTemplate({
        name: "",
        description: "",
        category: "Basic",
        isPremium: false,
        sports: [],
        isActive: true,
        type: "roster",
        layout: "one-page-per-team",
        selectedFields: [],
        headerText: "",
        footerText: "",
        price: 0
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  // Template builder functions
  const addField = (fieldName: string) => {
    if (!selectedFields.includes(fieldName)) {
      setSelectedFields([...selectedFields, fieldName]);
    }
  };

  const removeField = (fieldName: string) => {
    setSelectedFields(selectedFields.filter(field => field !== fieldName));
  };

  const moveFieldUp = (index: number) => {
    if (index > 0) {
      const newFields = [...selectedFields];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      setSelectedFields(newFields);
    }
  };

  const moveFieldDown = (index: number) => {
    if (index < selectedFields.length - 1) {
      const newFields = [...selectedFields];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      setSelectedFields(newFields);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSelectedFields((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleLogoUpload = async (file: File | string) => {
    if (typeof file === 'string') {
      setLogoUrl(file);
      setIsUploadingLogo(false);
      return;
    }

    try {
      setIsUploadingLogo(true);
      const uploadedUrl = await uploadProfileImage(file, 'packet-logos', 'avatar');
      setLogoUrl(uploadedUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateName || selectedFields.length === 0) {
      toast.error('Please provide a template name and select at least one field');
      return;
    }

    try {
      const templateData = {
        name: templateName,
        description: `Custom template with ${selectedFields.length} fields`,
        is_default: false,
        price: templatePrice,
        sports: ["All Sports"],
        is_active: true,
        layout: templateLayout,
        columns: selectedFields,
        header_text: headerText,
        footer_text: footerText,
        include_page_numbers: includePageNumbers,
        logo_url: logoUrl
      };

      if (editingTemplateId) {
        const { error } = await supabase
          .from('packet_templates')
          .update(templateData)
          .eq('id', editingTemplateId);
        
        if (error) throw error;
        toast.success('Template updated successfully');
      } else {
        const { error } = await supabase
          .from('packet_templates')
          .insert(templateData);
        
        if (error) throw error;
        toast.success('Template saved successfully');
      }

      clearTemplate();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const clearTemplate = () => {
    setTemplateName("");
    setTemplatePrice(0);
    setSelectedFields([]);
    setTemplateLayout('one-page-per-team');
    setHeaderText("");
    setFooterText("");
    setIncludePageNumbers(false);
    setLogoUrl("");
    setEditingTemplateId(null);
  };

  // Packet generation functions
  const generatePacketsForAllTeams = async () => {
    setIsGenerating(true);
    const filteredTeams = getFilteredTeams();
    const packets: {[key: string]: string} = {};

    try {
      for (const team of filteredTeams) {
        // Simulate packet generation
        await new Promise(resolve => setTimeout(resolve, 1000));
        packets[team.id] = `Generated packet for ${team.name}`;
      }
      
      setGeneratedPackets(packets);
      toast.success(`Generated ${filteredTeams.length} packets successfully`);
    } catch (error) {
      console.error('Error generating packets:', error);
      toast.error('Failed to generate packets');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadAllPackets = async () => {
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }

    if (!selectedPacketTemplate) {
      toast.error('Please select a template first');
      return;
    }

    setIsGenerating(true);

    try {

      // Step 1: Get selected template configuration
      const selectedTemplate = templates.find(t => t.id === selectedPacketTemplate);
      if (!selectedTemplate) {
        toast.error('Selected template not found');
        return;
      }

      // Step 2: Use already-fetched teams data (works with both old and new registration systems)
      const teamIds = teams
        .map(t => t.team_id)
        .filter(Boolean) as string[];

      if (teamIds.length === 0) {
        toast.error('No teams with valid team IDs found for this event');
        return;
      }

      console.log('📦 Generating packets for teams:', teamIds.length);

      // Step 3: Get rosters directly by team_id (not by coach email)
      const { data: teamRostersData, error: rostersError } = await supabase
        .from('coach_team_rosters')
        .select(`
          *,
          coach_teams!inner (
            id,
            name,
            head_coach_email,
            head_coach_name,
            head_coach_cell,
            age_group,
            sport_id,
            gender
          ),
          profiles:profile_id (
            status,
            user_id,
            username,
            name,
            role
          )
        `)
        .in('team_id', teamIds);

      if (rostersError) {
        console.error('Error fetching team rosters:', rostersError);
        toast.error('Failed to fetch team rosters');
        return;
      }

      // Handle case where teams exist but have no roster entries
      if (!teamRostersData || teamRostersData.length === 0) {
        console.warn('No roster entries found - teams have no players added yet');
        toast.error('No players found in team rosters. Teams exist but have no players added yet.');
        return;
      }

      // Step 4: Group data by teams
      interface TeamGroup {
        team: any;
        players: any[];
      }
      
      const teamGroups = teamRostersData.reduce((acc, roster) => {
        const teamId = roster.coach_teams?.id;
        if (!teamId) return acc;
        
        if (!acc[teamId]) {
          acc[teamId] = {
            team: roster.coach_teams,
            players: []
          };
        }
        acc[teamId].players.push(roster);
        return acc;
      }, {} as Record<string, TeamGroup>);

      // Step 5: Generate QR codes for all players
      const teamsArray: TeamGroup[] = Object.values(teamGroups);
      for (const teamGroup of teamsArray) {
        for (const player of teamGroup.players) {
          try {
            if (player.profiles?.username) {
              player.qrCode = await generateTeamQRCode(player.profile_id);
            } else {
              player.qrCode = null;
            }
          } catch (error) {
            console.error(`Error generating QR code for player ${player.id}:`, error);
            player.qrCode = null;
          }
        }
      }

      // Helper functions for template field processing
      const getFieldDisplayName = (fieldName: string): string => {
        return fieldName
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
          .trim();
      };

      const getDynamicColumns = () => {
        const templateFields = selectedTemplate?.selectedFields || [];
        // Filter out coach/team info columns that will be shown in header instead
        const headerFields = ['head_coach_name', 'head_coach_email', 'head_coach_cell', 'name', 'age_group'];
        const filteredFields = templateFields.filter(field => !headerFields.includes(field));
        return [...filteredFields, 'profile']; // Always include profile column
      };

      const getFieldValue = (athlete: any, fieldName: string): string => {
        if (fieldName === 'profile') {
          return 'profile';
        }

        if (athlete.hasOwnProperty(fieldName)) {
          const value = (athlete as any)[fieldName];
          return value || 'N/A';
        }

        if (athlete.coach_teams && athlete.coach_teams.hasOwnProperty(fieldName)) {
          const value = (athlete.coach_teams as any)[fieldName];
          return value || 'N/A';
        }

        switch (fieldName) {
          case 'team_name':
          case 'name':
            return athlete.coach_teams?.name || 'N/A';
          case 'head_coach_name':
            return athlete.coach_teams?.head_coach_name || 'N/A';
          case 'head_coach_email':
            return athlete.coach_teams?.head_coach_email || 'N/A';
          case 'head_coach_cell':
            return athlete.coach_teams?.head_coach_cell || 'N/A';
          case 'age_group':
            return athlete.coach_teams?.age_group || 'N/A';
          default:
            return 'N/A';
        }
      };

      // Step 6: Generate single HTML document with all teams
      const selectedEventData = events.find(e => e.id === selectedEvent);
      
      // Get template branding elements
      const headerText = selectedTemplate?.headerText || selectedTemplate?.name || 'Hoops';
      const footerText = selectedTemplate?.footerText || 'RecruitLook Team Roster';
      const logoUrl = selectedTemplate?.logoUrl;

      // Generate combined HTML content for all teams
      let allTeamsHTML = '';
      
      for (let teamIndex = 0; teamIndex < teamsArray.length; teamIndex++) {
        const { team, players } = teamsArray[teamIndex];

        // Generate QR codes for this team's players
        const athletesWithQR = await Promise.all(
          players.map(async (player) => {
            try {
              if (player.profiles?.username) {
                const qrCode = await generateTeamQRCode(player.profile_id);
                return { ...player, qrCode };
              } else {
                return { ...player, qrCode: null };
              }
            } catch (error) {
              console.error(`Error generating QR code for player ${player.id}:`, error);
              return { ...player, qrCode: null };
            }
          })
        );

        const confirmedCount = athletesWithQR.filter(a => a.qrCode !== null).length;
        
        // Add page break before each team (except the first)
        const pageBreak = teamIndex > 0 ? 'style="page-break-before: always;"' : '';
        
        // Generate HTML for this team
        allTeamsHTML += `
    <div class="team-page" ${pageBreak}>
        <div class="header">
            ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="template-logo" />` : ''}
            <div class="header-title">${headerText}</div>
            ${team.name ? `<div class="team-info">${team.name}${team.age_group ? ` - ${team.age_group}` : ''}</div>` : ''}
            ${team.head_coach_name || team.head_coach_email || team.head_coach_cell ? `
            <div class="coach-info" style="color: #444; font-size: 14px; margin-top: 8px;">
                ${team.head_coach_name ? `<strong>Coach:</strong> ${team.head_coach_name}` : ''}
                ${team.head_coach_email ? ` | <strong>Email:</strong> ${team.head_coach_email}` : ''}
                ${team.head_coach_cell ? ` | <strong>Phone:</strong> ${team.head_coach_cell}` : ''}
            </div>
            ` : ''}
        </div>
        
        <table class="roster-table">
            <thead>
                <tr>
                    ${getDynamicColumns().map(column => {
                      if (column === 'profile') {
                        return '<th>Profile</th>';
                      }
                      return `<th>${getFieldDisplayName(column)}</th>`;
                    }).join('')}
                </tr>
            </thead>
            <tbody>
                ${athletesWithQR.map(athlete => `
                    <tr>
                        ${getDynamicColumns().map(column => {
                          if (column === 'profile') {
                            return `<td style="text-align: center;">
                                ${athlete.qrCode ? `
                                    <img src="${athlete.qrCode}" alt="QR Code" style="width: 50px; height: 50px;" />
                                ` : `
                                    No Profile
                                `}
                            </td>`;
                          }
                          return `<td>${getFieldValue(athlete, column)}</td>`;
                        }).join('')}
                    </tr>
                `).join('')}
            </tbody>
        </table>
        
        <div class="footer">
            <p>${footerText}</p>
        </div>
    </div>`;
      }

      // Create single HTML document with all teams
      const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${headerText} - All Teams Roster</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            margin: 20px;
            background: white;
        }
        .team-page {
            min-height: 100vh;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
        }
        .template-logo {
            max-width: 150px;
            max-height: 80px;
            margin-bottom: 15px;
        }
        .header-title {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #333;
        }
        .team-info {
            font-size: 18px;
            color: #666;
            margin-bottom: 10px;
        }
        .roster-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
        }
        .roster-table th,
        .roster-table td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
        }
        .roster-table th {
            background-color: #f2f2f2;
            font-weight: bold;
        }
        .footer {
            margin-top: 40px;
            text-align: center;
            color: #666;
            font-size: 12px;
            border-top: 1px solid #ddd;
            padding-top: 20px;
        }
        @media print {
            body { margin: 0; }
            .team-page { 
                page-break-after: always; 
                min-height: 100vh;
            }
            .team-page:last-child {
                page-break-after: avoid;
            }
            /* Duplex printing support - alternating margins for binding */
            @page {
                size: letter;
                margin: 0.5in 0.75in 0.5in 0.75in;
            }
            @page :left {
                margin-left: 1in;
                margin-right: 0.5in;
            }
            @page :right {
                margin-left: 0.5in;
                margin-right: 1in;
            }
        }
    </style>
</head>
<body>
    <p style="text-align: center; font-size: 11px; color: #888; margin-bottom: 10px; display: none;" class="print-hint">
        Tip: Enable "Two-sided" or "Duplex" printing in your print dialog for best results
    </p>
    ${allTeamsHTML}
</body>
</html>`;

      // Create single window with all teams
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Trigger print dialog after content loads
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        toast.error('Unable to open print window. Please check your popup blocker settings.');
        return;
      }

      // Generate a simple PDF blob for purchasable packet functionality
      // Use a lightweight approach to avoid freezing the UI
      setTimeout(async () => {
        try {
          console.log('💾 Setting PDF blob in downloadAllPackets...');
          const eventData = events.find(e => e.id === selectedEvent);
          const filename = `${eventData?.name || 'event'}-all-teams-roster.pdf`;
          
          // Create a simple PDF blob from the HTML content
          const blob = new Blob([htmlContent], { type: 'text/html' });
          console.log('💾 Created blob:', blob.size, 'bytes');
          
          setGeneratedPdfBlob(blob);
          setGeneratedFilename(filename);
          console.log('💾 Set generatedPdfBlob and filename:', filename);
          
          toast.success("PDF ready for purchasable packet upload");
        } catch (error) {
          console.error('Error preparing packet:', error);
          toast.error('Failed to prepare packet for upload');
        }
      }, 1000);

      toast.success(`Generated PDF packet with ${teamsArray.length} teams using Print Roster format`);

    } catch (error) {
      console.error('Error generating PDF packet:', error);
      toast.error('Failed to generate PDF packet');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Packet Management</h1>
          <p className="text-muted-foreground">
            Create, manage, and generate team roster packets
          </p>
        </div>
      </div>

      <Tabs defaultValue="library" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="library">Template Library</TabsTrigger>
          <TabsTrigger value="builder">Template Builder</TabsTrigger>
          <TabsTrigger value="generate">Generate Packets</TabsTrigger>
        </TabsList>

        {/* Template Library Tab */}
        <TabsContent value="library">
          <TemplateLibraryTab
            templates={templates}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            loading={loading}
            filteredTemplates={filteredTemplates}
            editingTemplate={editingTemplate}
            setEditingTemplate={setEditingTemplate}
            newTemplate={newTemplate}
            setNewTemplate={setNewTemplate}
            editTemplate={editTemplate}
            duplicateTemplate={duplicateTemplate}
            deleteTemplate={deleteTemplate}
            updateTemplate={updateTemplate}
            createTemplate={createTemplate}
            categories={categories}
          />
        </TabsContent>

        {/* Template Builder Tab */}
        <TabsContent value="builder">
          <TemplateBuilderTab
            selectedFields={selectedFields}
            setSelectedFields={setSelectedFields}
            templateName={templateName}
            setTemplateName={setTemplateName}
            templatePrice={templatePrice}
            setTemplatePrice={setTemplatePrice}
            templateLayout={templateLayout}
            setTemplateLayout={setTemplateLayout}
            headerText={headerText}
            setHeaderText={setHeaderText}
            footerText={footerText}
            setFooterText={setFooterText}
            includePageNumbers={includePageNumbers}
            setIncludePageNumbers={setIncludePageNumbers}
            logoUrl={logoUrl}
            setLogoUrl={setLogoUrl}
            isUploadingLogo={isUploadingLogo}
            setIsUploadingLogo={setIsUploadingLogo}
            rosterFields={rosterFields}
            fieldCategories={fieldCategories}
            handleLogoUpload={handleLogoUpload}
            saveTemplate={saveTemplate}
            clearTemplate={clearTemplate}
            addField={addField}
            removeField={removeField}
            moveFieldUp={moveFieldUp}
            moveFieldDown={moveFieldDown}
            handleDragEnd={handleDragEnd}
            sensors={sensors}
          />
        </TabsContent>

        {/* Generate Packets Tab */}
        <TabsContent value="generate">
          <PacketGeneratorTab
            events={events}
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            loadingEvents={loadingEvents}
            teams={teams}
            loadingTeams={loadingTeams}
            selectedAgeGroup={selectedAgeGroup}
            setSelectedAgeGroup={setSelectedAgeGroup}
            eventAgeGroups={eventAgeGroups}
            teamsSummary={teamsSummary}
            templates={templates}
            selectedPacketTemplate={selectedPacketTemplate}
            setSelectedPacketTemplate={setSelectedPacketTemplate}
            isGenerating={isGenerating}
            setIsGenerating={setIsGenerating}
            generatedPackets={generatedPackets}
            setGeneratedPackets={setGeneratedPackets}
            getFilteredTeams={getFilteredTeams}
            generatePacketsForAllTeams={generatePacketsForAllTeams}
            downloadAllPackets={downloadAllPackets}
            setPurchasablePacket={setPurchasablePacket}
            generatedPdfBlob={generatedPdfBlob}
            generatedFilename={generatedFilename}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PacketManagementPage;